<template>
  <b-modal v-model="modal" :title="title" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true">
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveItemData()" autocomplete="off">
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label for="name">Nome</label>
                <input
                  id="name"
                  v-model="item.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': submitted && $v.item.name.$error }"
                  :disabled="!canManage"
                />
                <div v-if="submitted && !$v.item.name.required" class="invalid-feedback">
                  O campo nome é obrigatório.
                </div>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="form-group">
                <label for="identifier">{{ controlData.identifier_label || 'Identificador' }}</label>
                <input
                  id="identifier"
                  v-model="item.identifier"
                  v-mask="controlData.identifier_mask || ''"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': submitted && $v.item.identifier.$error }"
                  :disabled="!canManage"
                />
                <div v-if="submitted && !$v.item.identifier.required" class="invalid-feedback">
                  O campo é obrigatório.
                </div>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="form-group">
                <label for="limit_register">Limite de cadastros</label>
                <input
                  id="limit_register"
                  v-model="item.limit_register"
                  type="number"
                  class="form-control"
                  :disabled="!canManage"
                />
              </div>
            </div>
            <div class="col-sm-4" v-if="['per_group', 'per_item_and_group'].includes(controlData.vacancy_mode)">
              <div class="form-group">
                <label for="group_id">Grupo</label>
                <v-select
                  v-model="item.group_id"
                  :options="groupOptions"
                  :filterable="false"
                  :reduce="g => g.id"
                  label="name"
                  placeholder="Buscar grupo..."
                  :disabled="!canManage"
                  @search="onGroupSearch"
                  @open="onGroupOpen"
                >
                  <template #no-options="{ search }">
                    <span v-if="search">Nenhum grupo encontrado</span>
                    <span v-else>Digite para buscar...</span>
                  </template>
                </v-select>
              </div>
            </div>
            <div class="col-sm-4" v-if="!['per_group', 'per_item_and_group'].includes(controlData.vacancy_mode)">
              <div class="form-group">
                <label for="active">Status</label>
                <select
                  id="active"
                  v-model="item.active"
                  class="form-control"
                  :disabled="!canManage"
                >
                  <option :value="true">Ativo</option>
                  <option :value="false">Inativo</option>
                </select>
              </div>
            </div>
            <div class="col-sm-4" v-if="controlData.assign_participant_group">
              <div class="form-group">
                <label for="participant_group_id">Grupo do Participante</label>
                <select
                  id="participant_group_id"
                  v-model="item.participant_group_id"
                  class="form-control"
                  :disabled="!canManage"
                >
                  <option value="">Nenhum (usar padrão)</option>
                  <option v-for="g in participantGroupsList" :key="g.id" :value="g.id">{{ g.name }}</option>
                </select>
              </div>
            </div>
            <!-- Tags do Item: aplicadas automaticamente a quem se inscrever por este item -->
            <div class="col">
              <div class="form-group">
                <label for="tag_ids">Tags do Item</label>
                <v-select
                  id="tag_ids"
                  v-model="item.tag_ids"
                  :options="tagOptions"
                  :reduce="t => t.id"
                  label="name"
                  multiple
                  placeholder="Selecione as tags..."
                  :disabled="!canManage"
                >
                  <template #no-options>Nenhuma tag encontrada</template>
                </v-select>
                <small class="text-muted">
                  Todo participante que se inscrever por este item herda estas tags automaticamente.
                </small>
              </div>
            </div>
            <div class="col-sm-4" v-if="hasTagBundles">
              <div class="form-group">
                <label for="tag_bundle_id">Grupo de Tags</label>
                <select
                  id="tag_bundle_id"
                  v-model="item.tag_bundle_id"
                  class="form-control"
                  :disabled="!canManage"
                >
                  <option :value="null">— Nenhum —</option>
                  <option v-for="b in (controlData.tag_bundles || [])" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>
                <small v-if="selectedBundleTagsLabel" class="text-muted">{{ selectedBundleTagsLabel }}</small>
              </div>
            </div>
          </div>

          <!-- Dynamic fields from schema -->
          <div class="row" v-if="controlData.fields_schema && controlData.fields_schema.length > 0">
            <div class="col-12">
              <hr />
              <label class="text-muted">Campos Customizados</label>
            </div>
            <div class="col-sm-6" v-for="field in controlData.fields_schema" :key="field.name">
              <div class="form-group">
                <label :for="'data_' + field.name">{{ field.label }}</label>
                <input
                  v-if="field.type === 'text' || field.type === 'number'"
                  :id="'data_' + field.name"
                  v-model="itemDataFields[field.name]"
                  :type="field.type"
                  class="form-control"
                  :disabled="!canManage"
                />
                <select
                  v-else-if="field.type === 'select'"
                  :id="'data_' + field.name"
                  v-model="itemDataFields[field.name]"
                  class="form-control"
                  :disabled="!canManage"
                >
                  <option value="">Selecione...</option>
                  <option v-for="(opt, oi) in (field.options || [])" :key="oi" :value="opt">{{ opt }}</option>
                </select>
                <select
                  v-else-if="field.type === 'multiselect'"
                  :id="'data_' + field.name"
                  v-model="itemDataFields[field.name]"
                  class="form-control"
                  :disabled="!canManage"
                  multiple
                >
                  <option v-for="(opt, oi) in (field.options || [])" :key="oi" :value="opt">{{ opt }}</option>
                </select>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="close()">{{ canManage ? 'Cancelar' : 'Fechar' }}</b-button>
      <button v-if="canManage" type="submit" class="btn btn-primary mr-1" @click="saveItemData()">Salvar</button>
    </template>
  </b-modal>
</template>
<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { listGroups, listTags } from './services'

export default {
  components: {
    Loading,
    vSelect
  },
  props: {
    controlData: {
      type: Object,
      default: () => ({})
    },
    itemData: {
      type: Object,
      default: null
    },
    groupsList: {
      type: Array,
      default: () => []
    },
    participantGroupsList: {
      type: Array,
      default: () => []
    },
    canManage: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      title: 'Novo Item',
      loading: false,
      submitted: false,
      item: {
        name: '',
        identifier: '',
        limit_register: null,
        active: true,
        group_id: '',
        participant_group_id: '',
        tag_bundle_id: null,
        tag_ids: []
      },
      itemDataFields: {},
      modal: false,
      groupOptions: [],
      groupSearchTimeout: null,
      tagOptions: []
    }
  },
  computed: {
    hasTagBundles () {
      return Array.isArray(this.controlData.tag_bundles) && this.controlData.tag_bundles.length > 0
    },
    /**
     * Label de preview com nomes das tags do bundle selecionado.
     * Mostra abaixo do dropdown pra confirmar visualmente o que vai entrar.
     */
    selectedBundleTagsLabel () {
      if (!this.item.tag_bundle_id || !this.hasTagBundles) return ''
      const bundle = this.controlData.tag_bundles.find(b => Number(b.id) === Number(this.item.tag_bundle_id))
      if (!bundle || !Array.isArray(bundle.tag_ids) || bundle.tag_ids.length === 0) return ''
      return `${bundle.tag_ids.length} tag(s) serão atribuídas aos participantes deste item`
    }
  },
  validations: {
    item: {
      name: { required },
      identifier: { required }
    }
  },
  methods: {
    ...mapActions('registrationControl', {
      postItem: 'saveItem'
    }),
    open (data = null) {
      this.modal = true
      this.loading = false
      this.submitted = false
      this.loadTags()
      if (data) {
        this.title = this.canManage ? 'Editar Item' : 'Visualizar Item'
        this.item = {
          id: data.id,
          name: data.name,
          identifier: data.identifier,
          limit_register: data.limit_register,
          active: data.active,
          group_id: data.group_id || null,
          participant_group_id: data.participant_group_id || '',
          tag_bundle_id: data.tag_bundle_id || null,
          tag_ids: Array.isArray(data.tag_ids) ? data.tag_ids.map(Number) : []
        }
        // Garante que as tags já salvas apareçam mesmo antes do load terminar
        if (Array.isArray(data.tags) && data.tags.length) {
          this.tagOptions = data.tags
        }
        // Pre-populate vue-select with current group
        if (data.group_id && data.group_name) {
          this.groupOptions = [{ id: data.group_id, name: data.group_name }]
        } else {
          this.groupOptions = []
        }
        this.itemDataFields = data.data ? { ...data.data } : {}
        this.initMultiselectFields()
      } else {
        this.title = 'Novo Item'
        this.item = {
          name: '',
          identifier: '',
          limit_register: null,
          active: true,
          group_id: null,
          participant_group_id: '',
          tag_bundle_id: null,
          tag_ids: []
        }
        this.groupOptions = []
        this.itemDataFields = {}
        this.initMultiselectFields()
      }
    },
    async loadTags () {
      try {
        const { data } = await listTags({ limit: 1000 })
        this.tagOptions = data.data || data || []
      } catch (e) {
        // mantém as opções já hidratadas (data.tags) em caso de falha
      }
    },
    onGroupOpen () {
      if (this.groupOptions.length <= 1) {
        this.fetchGroups('')
      }
    },
    onGroupSearch (search, loading) {
      clearTimeout(this.groupSearchTimeout)
      if (!search) {
        this.fetchGroups('')
        return
      }
      loading(true)
      this.groupSearchTimeout = setTimeout(() => {
        this.fetchGroups(search).then(() => {
          loading(false)
        }).catch(() => {
          loading(false)
        })
      }, 300)
    },
    async fetchGroups (search) {
      const controlId = this.controlData.id || this.$route.params.controlId
      const params = { limit: 30 }
      if (search) {
        params.search = search
      }
      const res = await listGroups(controlId, params)
      this.groupOptions = res.data.data || []
    },
    initMultiselectFields () {
      if (this.controlData.fields_schema) {
        this.controlData.fields_schema.forEach(field => {
          if (field.type === 'multiselect' && !Array.isArray(this.itemDataFields[field.name])) {
            this.$set(this.itemDataFields, field.name, this.itemDataFields[field.name] ? [this.itemDataFields[field.name]] : [])
          }
        })
      }
    },
    async saveItemData () {
      this.submitted = true
      this.$v.item.$touch()
      if (this.$v.item.$invalid) {
        return
      }
      this.loading = true

      const payload = {
        ...this.item,
        data: this.itemDataFields,
        group_id: this.item.group_id || null,
        participant_group_id: this.item.participant_group_id || null,
        tag_bundle_id: this.item.tag_bundle_id || null,
        tag_ids: Array.isArray(this.item.tag_ids) ? this.item.tag_ids.map(Number) : []
      }

      const controlId = this.controlData.id || this.$route.params.controlId

      await this.postItem({ controlId: controlId, data: payload }).then(() => {
        successMessage('Sucesso: Dados armazenados com sucesso.', 2500)
        this.close()
        this.$emit('created')
      }).catch((err) => {
        errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
        this.loading = false
      })
    },
    close () {
      this.modal = false
      this.item = {
        name: '',
        identifier: '',
        limit_register: null,
        active: true,
        group_id: null,
        participant_group_id: '',
        tag_bundle_id: null,
        tag_ids: []
      }
      this.groupOptions = []
      this.itemDataFields = {}
      this.$v.$reset()
    }
  },
  watch: {
    modal () {
      if (!this.modal) {
        this.close()
      }
    }
  }
}
</script>
<style scoped>
.vld-overlay.is-full-page {
    z-index: 1051 !important;
}
</style>
